import ClearIcon from "@mui/icons-material/Clear";
import InfoIcon from "@mui/icons-material/Info";
import { Alert, Grid, IconButton, Typography } from "@mui/material";
import { debounce } from "lodash";
import { Observer, observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";
import { HierarchyService } from "services/HierarchyService";
import { AuthStore } from "stores/AuthStore";
import LocalStorage from "stores/LocalStorage";
import RootStore from "stores/RootStore";
import { useStore } from "utils/useStore";
import AcxMainTextField from "../AcxMainTextField";
import RecursiveTreeStore from "../AcxRecursiveTree/Stores/AcxRecursiveTreeStore";
import AcxSelectMulti from "../Select/BaseSelectComponents/AcxSelectMulti";
import {
    flattenHierarchyBranchesForSelection,
    IAcxHierarchyComponentProps,
    loadUserServiceHierarchiesHelper,
    orderHierarchyBranchesForSelection,
} from "./AcxHierarchySelector";

interface HierarchyCategoryOptions {
    value: string;
    label: string;
}

interface HierCategorySelectionProps {
    treeStore: RecursiveTreeStore;
    isSubCategoryComponent?: boolean;
    isHierarchyAllowed?: (hierarchyId: string) => boolean;
}

interface HierarchySearchInputProps {
    treeStore: RecursiveTreeStore;
    hierService: HierarchyService;
    availableHierarchyIds?: string[];
    isHierarchyAllowed: (hierarchyId: string) => boolean;
}

const HierCategorySelection: React.FC<HierCategorySelectionProps> = (props) => {
    const hierService = useMemo(() => new HierarchyService(), []);
    const getHierarchiesForCategory = props.isSubCategoryComponent
        ? (subcategory) =>
              hierService.searchHierarchiesBySubCategoryAsync(subcategory)
        : (category) => hierService.searchHierarchiesByCategoryAsync(category);

    const selectedCategories = props.isSubCategoryComponent
        ? props.treeStore.selectedSubcategories
        : props.treeStore.selectedCategories;
    const filterToAllowedHierarchies = (hierarchies) => {
        if (!props.isHierarchyAllowed) return hierarchies;
        return hierarchies.filter((hierarchy) =>
            props.isHierarchyAllowed?.(hierarchy.id),
        );
    };

    const defaultSelectedCategories = selectedCategories.map((category) => ({
        value: category,
        label: category,
    }));

    const [hierarchyCategories, setHierarchyCategories] = useState<
        HierarchyCategoryOptions[]
    >([]);

    useEffect(() => {
        async function getCategories() {
            const response = props.isSubCategoryComponent
                ? await hierService.getHierarchySubcategories()
                : await hierService.getHierarchyCategories();

            const hierarchyCategories = response.map(
                (value) =>
                    ({
                        value: value,
                        label: value,
                    } as HierarchyCategoryOptions),
            );
            setHierarchyCategories(hierarchyCategories);
        }

        getCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Observer key={`search`}>
            {() => (
                <Grid
                    item
                    xs={12}
                    key={`hierarchy-categories`}
                    marginTop={props.isSubCategoryComponent ? "4px" : "2px"}
                >
                    <AcxSelectMulti
                        id={
                            props.isSubCategoryComponent
                                ? "hierarchy-subcategory-selected"
                                : "hierarchy-category-selected"
                        }
                        options={hierarchyCategories}
                        defaultValue={defaultSelectedCategories}
                        valueField="value"
                        labelField="label"
                        inputLabel={
                            props.isSubCategoryComponent
                                ? "Hierarchy Subcategory"
                                : "Hierarchy Category"
                        }
                        onChange={(
                            categoryOptions?: HierarchyCategoryOptions[],
                        ) => {
                            // make sure we mark hierarchies from the categories in these arrays as selected
                            // filter out what categories are already selected?
                            const currentSelectedCategories =
                                categoryOptions?.map(
                                    (categoryOption) => categoryOption.value,
                                ) ?? [];
                            const missingCategories = selectedCategories.filter(
                                (category) =>
                                    !currentSelectedCategories?.includes(
                                        category,
                                    ),
                            );
                            const newCategories =
                                currentSelectedCategories.filter(
                                    (category) =>
                                        !selectedCategories.includes(category),
                                );

                            if (props.isSubCategoryComponent) {
                                props.treeStore.selectedSubcategories =
                                    currentSelectedCategories;
                            } else {
                                props.treeStore.selectedCategories =
                                    currentSelectedCategories;
                            }

                            // add new
                            newCategories?.forEach((category) => {
                                getHierarchiesForCategory(category).then(
                                    (hierarchies) => {
                                        let hierarchyIdsToSelect: string[] = [];
                                        let hierarchyLabelsToSelect: string[] =
                                            [];
                                        filterToAllowedHierarchies(
                                            hierarchies.childrenHierarchies,
                                        ).forEach((hierarchy) => {
                                                hierarchyIdsToSelect.push(
                                                    hierarchy.id,
                                                );
                                                hierarchyLabelsToSelect.push(
                                                    hierarchy.name,
                                                );
                                            });
                                        props.treeStore.addMultipleSelectedBranches(
                                            hierarchyIdsToSelect,
                                            hierarchyLabelsToSelect,
                                        );
                                    },
                                );
                            });

                            // remove old
                            missingCategories?.forEach((category) => {
                                getHierarchiesForCategory(category).then(
                                    (hierarchies) => {
                                        let hierarchyIdsToSelect: string[] = [];
                                        let hierarchyLabelsToSelect: string[] =
                                            [];
                                        filterToAllowedHierarchies(
                                            hierarchies.childrenHierarchies,
                                        ).forEach((hierarchy) => {
                                                hierarchyIdsToSelect.push(
                                                    hierarchy.id,
                                                );
                                                hierarchyLabelsToSelect.push(
                                                    hierarchy.name,
                                                );
                                            });
                                        props.treeStore.removeMultipleSelectedBranches(
                                            hierarchyIdsToSelect,
                                            hierarchyLabelsToSelect,
                                        );
                                    },
                                );
                            });
                        }}
                        containerHeight="32px"
                        maxContainerHeight="32px"
                        fullWidth
                        error={false}
                        // helperText={}
                    />
                </Grid>
            )}
        </Observer>
    );
};

const HierarchySearchInput: React.FC<HierarchySearchInputProps> = observer(
    ({ treeStore, hierService, availableHierarchyIds, isHierarchyAllowed }) => {
        const performHierarchyNameSearch = useMemo(
            () =>
                debounce((searchValue: string) => {
                    treeStore.setIsTreeLoading(true);

                    if (searchValue !== "") {
                        hierService
                            .searchHierarchiesForUserAsync(searchValue)
                            .then((res) => {
                                treeStore.setTreeSearchResponse(
                                    res.childrenHierarchies.filter((hierarchy) =>
                                        isHierarchyAllowed(hierarchy.id),
                                    ),
                                );
                            })
                            .finally(() => treeStore.setIsTreeLoading(false));
                    } else {
                        loadUserServiceHierarchiesHelper(
                            hierService,
                            treeStore,
                            availableHierarchyIds,
                        );
                        treeStore.setTreeSearchResponse([]);
                    }
                }, 500),
            [availableHierarchyIds, hierService, isHierarchyAllowed, treeStore],
        );

        useEffect(() => {
            return () => {
                performHierarchyNameSearch.cancel();
            };
        }, [performHierarchyNameSearch]);

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const nextSearchValue = e.target.value;

            treeStore.setSearchString(nextSearchValue);
            performHierarchyNameSearch(nextSearchValue);
        };

        const clearInput = () => {
            performHierarchyNameSearch.cancel();
            treeStore.setSearchString("");
            loadUserServiceHierarchiesHelper(
                hierService,
                treeStore,
                availableHierarchyIds,
            );
            treeStore.setTreeSearchResponse([]);
        };

        return (
            <Observer>
                {() => (
                    <Grid item xs={12} key={`search`}>
                        <AcxMainTextField
                            id="hierarchy-input-search"
                            value={treeStore.searchString}
                            placeholderText={
                                treeStore.searchString
                                    ? ""
                                    : "Search hierarchies..."
                            }
                            onChange={handleInputChange}
                            endAdornment={
                                <IconButton size="small" onClick={clearInput}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            }
                            fullWidth
                        />
                    </Grid>
                )}
            </Observer>
        );
    },
);

const localKey = "hierarchyWarningCount";

const HierarchySelectorHeader: React.FC<IAcxHierarchyComponentProps> = observer(
    (props: IAcxHierarchyComponentProps) => {
        const [showHeaderWarning, setShowHeaderWarning] =
            useState<boolean>(false);
        const hierService = useMemo(() => new HierarchyService(), []);
        const authStore = useStore(AuthStore);
        const availableHierarchyIdSet = useMemo(
            () =>
                new Set(
                    (props.availableHierarchyIds ?? []).map((id) => `${id}`),
                ),
            [props.availableHierarchyIds],
        );
        const hasAvailableHierarchyRestriction =
            availableHierarchyIdSet.size > 0;
        const isHierarchyAllowed = (hierarchyId: string) => {
            if (!hasAvailableHierarchyRestriction) return true;
            return availableHierarchyIdSet.has(`${hierarchyId}`);
        };

        const closeHeaderWarning = () => {
            setShowHeaderWarning(false);
        };

        const hasSearchResults: boolean = Boolean(
            props.treeStore.treeSearchResponse?.length !== 0,
        );

        const handleOnSelectAllResults = () => {
            const displayOrderedSearchResults =
                props.treeStore.topLevelBranches?.length
                    ? flattenHierarchyBranchesForSelection(
                          props.treeStore.topLevelBranches,
                      )
                    : orderHierarchyBranchesForSelection(
                          props.treeStore.treeSearchResponse,
                      );
            const hasPermsChildrenHierarchies =
                displayOrderedSearchResults.filter(
                    (branch) =>
                        branch.hasPerms !== false &&
                        isHierarchyAllowed(branch.id),
                );

            props.treeStore.addMultipleSelectedBranches(
                hasPermsChildrenHierarchies.map((branch) => branch.id),
                hasPermsChildrenHierarchies.map((branch) => branch.name),
            );
        };

        const HierSelectionWarning: React.FC = () => (
            <Observer key={`warning`}>
                {() => (
                    <Grid
                        item
                        container
                        direction="row"
                        wrap="nowrap"
                        justifyContent="flex-start"
                        alignContent="center"
                        alignItems="center"
                        sx={{
                            borderRadius: "4px",
                            backgroundColor: "#DBEAFE",
                            padding: "6px 16px",
                            maxHeight: "70px",
                        }}
                    >
                        <Grid item xs={2}>
                            <InfoIcon color="secondary" />
                        </Grid>
                        <Grid item xs={8}>
                            <h4 style={{ color: "#1E3A8A", fontSize: "11px" }}>
                                Children are not selected by default.
                                <br />
                                Please confirm selection.
                            </h4>
                        </Grid>
                        <Grid item container xs={2} justifyContent="flex-end">
                            <IconButton
                                size="small"
                                onClick={closeHeaderWarning}
                            >
                                <ClearIcon fontSize="small" color="secondary" />
                            </IconButton>
                        </Grid>
                    </Grid>
                )}
            </Observer>
        );

        const HierSelectionDetails: React.FC = () => (
            <Observer key={`search`}>
                {() => (
                    <Grid
                        item
                        container
                        justifyContent="space-between"
                        wrap="nowrap"
                        sx={{ paddingTop: "4px" }}
                    >
                        <Typography
                            sx={{
                                display: "flex",
                                flexWrap: "nowrap",
                                alignItems: "center",
                                textWrap: "nowrap",
                            }}
                        >
                            <b
                                style={{
                                    marginRight: "5px",
                                }}
                            >
                                Total selected:
                            </b>
                            {props.treeStore.selectedBranchIds.length}
                        </Typography>
                        {!props.viewOnly && (
                            <Grid
                                item
                                container
                                justifyContent="flex-end"
                                wrap="nowrap"
                            >
                                <Typography
                                    sx={{
                                        padding: "8px 0px",
                                        fontWeight: "bold",
                                        textAlign: "end",
                                        cursor: hasSearchResults
                                            ? props.treeStore.isSelectionLimitReached()
                                                ? "default"
                                                : "pointer"
                                            : "default",
                                        color: hasSearchResults
                                            ? props.treeStore.isSelectionLimitReached()
                                                ? "#AFB4BE"
                                                : "#3564D5"
                                            : "#AFB4BE",
                                    }}
                                    onClick={
                                        hasSearchResults &&
                                        !props.treeStore.isSelectionLimitReached()
                                            ? handleOnSelectAllResults
                                            : () => {}
                                    }
                                >
                                    Select All Results
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                )}
            </Observer>
        );

        const HierSelectionLimitNotice: React.FC = () => (
            <Observer>
                {() =>
                    props.maxSelectedHierarchyCount &&
                    props.selectionLimitMessage &&
                    !props.viewOnly ? (
                        <Grid item xs={12}>
                            <Alert
                                severity={
                                    props.treeStore.isSelectionLimitReached()
                                        ? "warning"
                                        : "info"
                                }
                                sx={{
                                    alignItems: "center",
                                    padding: "2px 12px",
                                    "& .MuiAlert-message": {
                                        padding: "4px 0",
                                    },
                                }}
                            >
                                {props.selectionLimitMessage}
                            </Alert>
                        </Grid>
                    ) : null
                }
            </Observer>
        );

        useEffect(() => {
            const local = RootStore().getStore(LocalStorage);
            const localStore = local.getLocalStore("AcxDefault");
            localStore.getItem(localKey).then((item: any) => {
                if (!item) {
                    localStore.setItem(localKey, 1);
                } else if (item < 6) {
                    localStore.setItem(localKey, item + 1);
                    props.treeStore.headerWarningHasRendered &&
                        setShowHeaderWarning(true);
                }
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [props.treeStore.headerWarningHasRendered]);

        return (
            <Grid
                item
                container
                justifyContent="center"
                xs={12}
                sx={{
                    padding: "6px 10px 2px 10px",
                    rowGap: "2px",
                    "& .MuiInputLabel-root": {
                        marginBottom: "2px",
                    },
                }}
            >
                {!props.viewOnly && (
                    <HierarchySearchInput
                        treeStore={props.treeStore}
                        hierService={hierService}
                        availableHierarchyIds={props.availableHierarchyIds}
                        isHierarchyAllowed={isHierarchyAllowed}
                    />
                )}
                {authStore.canUserEdit("Hierarchy Category Search") &&
                    !props.hideCategorySelectors && (
                    <>
                        <HierCategorySelection
                            treeStore={props.treeStore}
                            isHierarchyAllowed={isHierarchyAllowed}
                        />
                        <HierCategorySelection
                            isSubCategoryComponent={true}
                            treeStore={props.treeStore}
                            isHierarchyAllowed={isHierarchyAllowed}
                        />
                    </>
                )}
                <HierSelectionDetails />
                <HierSelectionLimitNotice />
                {showHeaderWarning && <HierSelectionWarning />}
            </Grid>
        );
    },
);

export default HierarchySelectorHeader;
